/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import React from "react";
import GateLogCard from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/Details/Sections/GateLogCard";
import {CustomTextField} from "../../../../../../../components/CustomTextField";
import CustomPhotoPickerMultiple from "../../../../../../../components/PhotoPicker/CustomPhotoPickerMultiple";
import CustomFileCard from "../../../../../../../components/CustomFileCard";
import NotInformation from "../../../../../../../components/NotInformation";
import {
    DeleteIconsOptions,
    DownloadIcon,
    IconCamera,
    IconDocumentVehicleGate
} from "../../../../../../../components/buttons/IconButtons";
import {FILE_TYPE} from "../../../../../../../Helpers/fileType";
import {fileSize} from "../../../../../../../constant/variable";
import {downloadActionFile, isNotEmpty} from "../../../../../../../Apis/functions";
import {GATE_LOG_NOTES_LENGTH} from "../Model/Model";

/**
 * Gate log the guard captures when the vehicle arrives at the plant.
 *
 * Every form type is closed with the same log, so the notes of the movement
 * and the evidence that supports it are declared once and each detail only
 * renders the section.
 *
 * The log of a movement of the gate is read as the groups it is written with, so
 * there the notes and the evidence live in a block of their own, the way the
 * vehicle does; the card that only holds what the dispatch wrote reads them
 * straight in it, because it groups nothing else.
 *
 * A movement that is already closed has nothing left to capture, so the log is
 * only read: the notes are disabled and the evidence is listed as file cards
 * instead of the picker that attaches them.
 *
 * @param {Object} props - Values of the gate log and its handlers.
 * @param {Function} props.t - Translation handler.
 * @param {Object} props.gateLog - Gate log captured by the guard.
 * @param {Function} props.handleChange - Change handler of the fields of the gate log.
 * @param {Object} props.photoPicker - Evidence of the movement captured at the gate.
 * @param {Function} props.setPhotoPicker - State updater of the evidence of the gate.
 * @param {boolean} props.emptyFields - Whether the required fields are marked.
 * @param {boolean} props.disabled - Whether the gate log is only read.
 * @param {string} props.title - Title the card of the log is named with.
 * @param {string} props.noteName - Field of the log the note is written in.
 * @param {string} props.noteTrans - Label the note is read with.
 * @param {string} props.notePlaceholder - Placeholder that says what the note holds.
 * @param {string} props.evidenceTrans - Label the evidence is read with.
 * @param {React.ReactNode} props.transportSection - Vehicle the movement was
 * read with, displayed as the first section of the log, because the guard reads
 * the unit before writing what was seen of it.
 * @param {boolean} props.evidenceCard - Whether the notes and the evidence are
 * read in a block of their own, the way the movement of the gate groups them;
 * the card that only holds them writes them straight in it.
 * @param {Object} props.contentRef - Reference of the content of the card.
 *
 * @returns {JSX.Element} Card with the notes and the evidence of the gate.
 */
const GuardGateLogSection = ({
                                 t,
                                 gateLog,
                                 handleChange,
                                 photoPicker,
                                 setPhotoPicker,
                                 emptyFields,
                                 disabled = false,
                                 title = 'gate_log',
                                 contentRef,
                                 icon=IconDocumentVehicleGate,
                                 noteName = 'notes',
                                 noteTrans = 'notes',
                                 notePlaceholder = 'add_details_about_the_vehicle_condition_seals_or_anything_unusual_upon_arrival',
                                 evidenceTrans = 'supporting_evidence',
                                 transportSection = null,
                                 evidenceCard = true
                             }) => {

    const evidence = gateLog?.evidence || [];
    const notesClassName = evidenceCard ? 'row input-padding-top' : 'row mt-4';
    const evidenceClassName = evidenceCard ? 'row pb-3' : 'row my-3';

    const notesAndEvidence = (
        <>
            <div className={notesClassName}>
                <div className={"col-12"}>
                    <CustomTextField
                        length={GATE_LOG_NOTES_LENGTH}
                        id={`txt_ggls_${noteName}`}
                        name={noteName}
                        value={gateLog?.[noteName]}
                        onChange={handleChange}
                        trans={noteTrans}
                        required={!disabled}
                        disabled={disabled}
                        multiline={true}
                        row={4}
                        emptyFields={emptyFields}
                        valueEmpty={!isNotEmpty(gateLog?.[noteName])}
                        placeholder={t(notePlaceholder)}
                    />
                </div>
            </div>
            <div className={evidenceClassName}>
                <div className={"col-12"}>
                    <p className={"font-color-general-950 fs-14px ibl-font-w-600 mb-3"}>
                        {t(evidenceTrans)}{disabled ? '' : ' *'}
                    </p>
                    {/* A log that is only read carries the evidence the gate
                    attached to it, so the one that was closed with none says
                    so instead of leaving the section empty. */}
                    {disabled && !evidence.length &&
                        <NotInformation text={t('no_evidence_added')} card={true}/>
                    }
                    {disabled ?
                        evidence?.map((file) => (
                            <div className={"mt-2"} key={file?.id}>
                                <CustomFileCard
                                    Icon={file?.Icon}
                                    title={file?.name}
                                    subtitle={
                                        <p className={'justify-content-start d-flex ibl-font-w-400 fs-10px'}>
                                            {file?.size}
                                        </p>
                                    }
                                    fileValue={file?.filePath}
                                    buttonId={'btn_ggls_download_' + file?.id}
                                    downloadIcon={DownloadIcon}
                                    onClick={() => downloadActionFile(file?.filePath)}
                                    showBtnDownload={true}
                                />
                            </div>
                        ))
                        :
                        <CustomPhotoPickerMultiple
                            id={'fu_ggls_file'}
                            photoPickerFile={photoPicker}
                            setPhotoPickerFile={setPhotoPicker}
                            accept={{...FILE_TYPE.IMAGE_FILES, size: fileSize}}
                            titleInput={'take_photography'}
                            showBtnCamera={true}
                            // The evidence of the gate is what the guard sees
                            // when the vehicle arrives, so it is taken with the
                            // camera and no file of the device is attached
                            showBtnUpload={false}
                            showBtnDeleteItemGallery={true}
                            showBtnDownload={true}
                            deleteIcon={DeleteIconsOptions}
                            downloadIcon={DownloadIcon}
                            apiDelete={false}
                            required={true}
                            emptyFields={emptyFields && photoPicker?.listFiles?.length === 0}
                        />
                    }
                </div>
            </div>
        </>
    );

    return (
        <GateLogCard
            t={t}
            Icon={icon}
            title={title}
            contentRef={contentRef}
        >
            {transportSection &&
                <div className={"px-3"}>
                    {transportSection}
                </div>
            }
            {evidenceCard ?
                <div className={"px-3"}>
                    <div className="border-full-shades-300 background-general-00 border-radius-8px my-3">
                        <div className={"d-flex align-items-center p-2"}>
                            <div className={"mr-2 d-flex align-items-center"}><IconCamera/></div>
                            <p className={"fs-14px ibl-font-w-600"}>{t("evidence")}</p>
                        </div>
                        <div className={"border-bottom-shades-300"}/>

                        {notesAndEvidence}
                    </div>
                </div>
                :
                notesAndEvidence
            }
        </GateLogCard>
    );
};

export default GuardGateLogSection;
